import { z } from "zod";
import { TRAINING_COURSES } from "./trainingContent";

export { TRAINING_COURSES };

/*
 * Online-Schulungen (Unterweisungen am Bildschirm). Jede Schulung besteht aus Abschnitten: erst ein kurzer Film
 * (eigenes Video der Firma oder der eingebaute Kapitel-Film aus Folien), dann Fragen. Wer eine Frage falsch beantwortet,
 * sieht den Abschnitt noch einmal. Sind alle Abschnitte bestanden, bestätigt die Person mit ihrer Unterschrift und
 * HandVero erstellt den Teilnahmenachweis als PDF.
 *
 * Die richtigen Antworten stehen nur hier und werden vom Server nie an den Browser geschickt (siehe publicCourse).
 */

/** Kennzeichen der Vero-Szene, die zur Folie gespielt wird. */
export type VeroScene = "hello" | "point" | "alarm" | "walk" | "think" | "check";

export interface TrainingSlide {
  title: string;
  text: string;
  /** Sicherheitszeichen, die zur Folie eingeblendet werden (siehe PICTOS). */
  pictos?: string[];
  scene?: VeroScene;
}

export interface TrainingQuestion {
  id: string;
  text: string;
  options: string[];
  /** Nummern der richtigen Antworten; mehr als eine heißt Mehrfachauswahl. */
  correct: number[];
  /** Sicherheitszeichen, das über der Frage gezeigt wird („Was bedeutet dieses Zeichen?“). */
  picto?: string;
}

/**
 * basis: feste Pflichtinhalte, jede Schulung enthält sie; optional: festes Modul, das die Firma (oder je Person) zu- oder abschaltet;
 * custom: Abschnitt, den die Firma selbst geschrieben hat.
 */
export type SectionKind = "basis" | "optional" | "custom";

export interface TrainingSection {
  id: string;
  kind: SectionKind;
  /** Welche Mindestthemen der Berufsgenossenschaften der Abschnitt abdeckt (siehe TRAINING_BG_TOPICS). */
  topics?: string[];
  title: string;
  /** Ein Satz, steht im Inhaltsverzeichnis und im Nachweis. */
  summary: string;
  slides: TrainingSlide[];
  questions: TrainingQuestion[];
  /** Offizielle Seiten, mit denen der Inhalt abgeglichen ist (Berufsgenossenschaften, DGUV, Regeln). */
  sources?: string[];
  /** Hilfetext für das Feld, in dem die Firma eigene Hinweise ergänzt. */
  noteHint: string;
}

export interface TrainingCourse {
  id: string;
  /** Steigt, wenn sich Inhalte oder Fragen ändern; steht im Nachweis. */
  version: number;
  title: string;
  kicker: string;
  description: string;
  minutes: number;
  /** Nur benannt, nie zitiert. */
  basis: string[];
  confirm: string;
  sections: TrainingSection[];
}

/* ---------- Was das Gesetz verlangt ---------- */

export interface TrainingLawItem {
  title: string;
  text: string;
  /** Wie HandVero dabei hilft. */
  handvero?: string;
  source: string;
}

/** Zusammenfassung in einfachen Worten; Stand Oktober 2026. Keine Rechtsberatung. */
export const TRAINING_LAW: TrainingLawItem[] = [
  {
    title: "Wer unterweist?",
    text:
      "Der Arbeitgeber muss seine Beschäftigten ausreichend und angemessen über Sicherheit und Gesundheitsschutz unterweisen, während der Arbeitszeit. " +
      "Er kann die Aufgabe an eine fachkundige Person übertragen, bleibt aber verantwortlich.",
    handvero: "Tragen Sie bei jeder Schulung die verantwortliche Person ein. Sie steht im Nachweis und ist für Rückfragen sichtbar.",
    source: "§ 12 Arbeitsschutzgesetz",
  },
  {
    title: "Wann und wie oft?",
    text:
      "Vor Beginn der Tätigkeit, bei neuen Aufgaben, neuen Arbeitsmitteln oder neuer Technik und danach regelmäßig, mindestens einmal im Jahr. " +
      "Jugendliche unter 18 Jahren mindestens alle sechs Monate.",
    handvero: "Jeder Nachweis hat ein Ablaufdatum. Die Übersicht zeigt, wer wieder an der Reihe ist.",
    source: "§ 12 Arbeitsschutzgesetz, § 4 DGUV Vorschrift 1, § 29 Jugendarbeitsschutzgesetz",
  },
  {
    title: "Was gehört hinein?",
    text:
      "Die Gefahren am konkreten Arbeitsplatz und die Schutzmaßnahmen dagegen, so wie sie sich aus der Gefährdungsbeurteilung und den Betriebsanweisungen ergeben. " +
      "Eine allgemeine Schulung reicht deshalb allein nicht; sie muss auf den Betrieb zugeschnitten sein.",
    handvero:
      "Die festen Pflichtinhalte kommen von HandVero und lassen sich nicht ändern. Gefahren, Laufwege, Maschinen und Besonderheiten Ihres Betriebs ergänzen Sie selbst: als Hinweise, eigene Fragen und eigene Abschnitte.",
    source: "§§ 5 und 12 Arbeitsschutzgesetz",
  },
  {
    title: "Geht das am Bildschirm?",
    text:
      "Ja, als Hilfsmittel. Die Unfallversicherung nennt drei Bedingungen: Die Inhalte passen zum Arbeitsplatz, das Verständnis wird abgefragt, " +
      "und die Beschäftigten können jederzeit mit einer fachkundigen Person sprechen. Das persönliche Gespräch ersetzt eine Schulung am Bildschirm nicht ganz.",
    handvero: "Jeder Abschnitt endet mit Fragen. Wer Fehler macht, wiederholt den Abschnitt. Ansprechpartner und Telefon stehen auf jeder Seite.",
    source: "DGUV Information 211-005",
  },
  {
    title: "Wo es nicht reicht",
    text:
      "Bei Gefahrstoffen und biologischen Arbeitsstoffen ist eine mündliche Unterweisung anhand der Betriebsanweisung vorgeschrieben, mit Unterschrift der Beschäftigten. " +
      "Manche Tätigkeiten brauchen außerdem praktische Übungen oder eine eigene Ausbildung, etwa Gabelstapler fahren, Schutzausrüstung gegen Absturz oder Arbeiten an elektrischen Anlagen.",
    handvero: "Nutzen Sie dafür zusätzlich die Protokoll-Vorlage „Unterweisung im Arbeitsschutz“, die Teilnehmer vor Ort unterschreiben.",
    source: "§ 14 Gefahrstoffverordnung, § 14 Biostoffverordnung",
  },
  {
    title: "Nachweis",
    text:
      "Die Unterweisung muss dokumentiert werden: wer, wann, welche Inhalte. Eine Unterschrift der Teilnehmer wird empfohlen. " +
      "Bewahren Sie Nachweise mindestens bis zur nächsten Unterweisung auf, besser mehrere Jahre, damit Sie nach einem Unfall belegen können, dass unterwiesen wurde.",
    handvero: "HandVero erstellt nach bestandener Schulung ein PDF mit Name, Inhalten, Ergebnis, Datum, Unterschrift und Prüfsumme.",
    source: "§ 4 DGUV Vorschrift 1, § 6 Arbeitsschutzgesetz",
  },
];

export const TRAINING_DISCLAIMER =
  "HandVero stellt diese Schulung als Vorlage und Arbeitshilfe bereit und erbringt keine Rechtsberatung. Sie ersetzt weder die Gefährdungsbeurteilung " +
  "noch die Verantwortung des Arbeitgebers für eine vollständige, auf den Arbeitsplatz bezogene Unterweisung. Ob Inhalt und Form im Einzelfall genügen, " +
  "prüft der Verwender selbst. Gesetze und Regeln sind nur benannt; maßgeblich ist die jeweils gültige Fassung.";

export const TRAINING_DISCLAIMER_SHORT = "Schulungsvorlage von HandVero. Ob sie im Einzelfall genügt, prüft der Verwender selbst.";

export const TRAINING_ELEARNING_NOTE =
  "Die Schulung am Bildschirm ergänzt die persönliche Unterweisung. Rückfragen beantwortet die verantwortliche Person. " +
  "Für Gefahrstoffe und biologische Arbeitsstoffe ist zusätzlich eine mündliche Unterweisung vorgeschrieben.";

export function trainingCourse(id: string): TrainingCourse | undefined {
  return TRAINING_COURSES.find((c) => c.id === id);
}

/* ---------- Sicherheitszeichen ---------- */

export type PictoKind = "rescue" | "fire" | "mandatory" | "prohibition" | "warning" | "ghs";

export interface PictoInfo {
  label: string;
  kind: PictoKind;
}

/**
 * Sicherheitszeichen, die in Schulungen vorkommen. Gezeichnet werden sie in der App (apps/web/src/app/Pictograms.tsx)
 * als Nachbildungen nach Form und Farbe der Sicherheitskennzeichnung (ISO 3864, ISO 7010, ASR A1.3) und der Gefahrstoff-Piktogramme (CLP/GHS).
 */
export const PICTOS: Record<string, PictoInfo> = {
  fluchtweg: { label: "Rettungsweg, Notausgang", kind: "rescue" },
  "erste-hilfe": { label: "Erste Hilfe", kind: "rescue" },
  notruftelefon: { label: "Notruftelefon", kind: "rescue" },
  sammelstelle: { label: "Sammelstelle", kind: "rescue" },
  feuerloescher: { label: "Feuerlöscher", kind: "fire" },
  brandmelder: { label: "Brandmelder", kind: "fire" },
  "rauchen-verboten": { label: "Rauchen verboten", kind: "prohibition" },
  "feuer-verboten": { label: "Feuer, offenes Licht und Rauchen verboten", kind: "prohibition" },
  "aufzug-brand": { label: "Aufzug im Brandfall nicht benutzen", kind: "prohibition" },
  gehoerschutz: { label: "Gehörschutz benutzen", kind: "mandatory" },
  augenschutz: { label: "Augenschutz benutzen", kind: "mandatory" },
  kopfschutz: { label: "Kopfschutz benutzen", kind: "mandatory" },
  fussschutz: { label: "Fußschutz benutzen", kind: "mandatory" },
  handschutz: { label: "Handschutz benutzen", kind: "mandatory" },
  atemschutz: { label: "Atemschutz benutzen", kind: "mandatory" },
  schutzkleidung: { label: "Schutzkleidung benutzen", kind: "mandatory" },
  "haende-waschen": { label: "Hände waschen", kind: "mandatory" },
  "warn-allgemein": { label: "Allgemeine Gefahrenstelle", kind: "warning" },
  "warn-spannung": { label: "Gefährliche elektrische Spannung", kind: "warning" },
  "warn-stolpern": { label: "Hindernisse am Boden, Stolpergefahr", kind: "warning" },
  "warn-rutschen": { label: "Rutschgefahr", kind: "warning" },
  "ghs-explosiv": { label: "Explosiv", kind: "ghs" },
  "ghs-flamme": { label: "Entzündbar", kind: "ghs" },
  "ghs-brandfoerdernd": { label: "Brandfördernd", kind: "ghs" },
  "ghs-gasflasche": { label: "Gas unter Druck", kind: "ghs" },
  "ghs-aetzend": { label: "Ätzend", kind: "ghs" },
  "ghs-giftig": { label: "Giftig", kind: "ghs" },
  "ghs-reizend": { label: "Reizend, gesundheitsschädlich", kind: "ghs" },
  "ghs-gesundheit": { label: "Schwere Gesundheitsgefahr", kind: "ghs" },
  "ghs-umwelt": { label: "Umweltgefährlich", kind: "ghs" },
};

export const PICTO_KIND_LABEL: Record<PictoKind, string> = {
  rescue: "Rettungszeichen",
  fire: "Brandschutzzeichen",
  mandatory: "Gebotszeichen",
  prohibition: "Verbotszeichen",
  warning: "Warnzeichen",
  ghs: "Gefahrstoff-Piktogramm",
};

export const PICTO_NOTE =
  "Die Sicherheitszeichen stammen von der Berufsgenossenschaft Holz und Metall (BGHM). " +
  "Für Aushänge und Beschilderung im Betrieb verwenden Sie bitte die Original-Schilder aus dem Fachhandel.";

/* ---------- Mindestthemen der Berufsgenossenschaften ---------- */

export interface BgTopic {
  id: string;
  title: string;
  /** Abschnitte von HandVero, die das Thema abdecken (es genügt einer davon). */
  sections: string[];
  /** Thema, das nur die Firma selbst füllen kann (eigene Räume, Maschinen, Laufwege). */
  company?: boolean;
}

/** Themen der Erstunterweisung nach dem Nachweisbuch der BG ETEM; ähnliche Listen führen andere Berufsgenossenschaften. */
export const TRAINING_BG_TOPICS: BgTopic[] = [
  { id: "ziel", title: "Ziel: Sicherheit und Gesundheitsschutz im Betrieb", sections: ["pflichten"] },
  { id: "gefahren", title: "Mögliche Gefahren in Ihrem Arbeitsbereich", sections: [], company: true },
  { id: "maschinen", title: "Bedienung von Maschinen und Anlagen, Verwendung von Werkzeugen", sections: ["elektrisch"], company: true },
  { id: "arbeitsstoffe", title: "Gefahren beim Umgang mit Arbeitsstoffen", sections: ["gefahrstoffe"] },
  { id: "ordnung", title: "Ordnung und Sauberkeit am Arbeitsplatz", sections: ["ordnung"] },
  { id: "unfaelle", title: "Verhalten bei Unfällen, Bränden und Schadensfällen", sections: ["notfall", "brandschutz"] },
  { id: "maengel", title: "Verhalten bei Mängeln der Arbeitssicherheit und des Brandschutzes", sections: ["pflichten"] },
  { id: "rettungswege", title: "Rettungswege und Notausgänge", sections: ["rettungswege"] },
  { id: "standorte", title: "Standorte von Verbandkasten, Feuerlöscher und Meldern", sections: ["notfall", "brandschutz"], company: true },
  { id: "doku-eh", title: "Dokumentation von Erste-Hilfe-Leistungen", sections: ["notfall"] },
  { id: "ansprechpersonen", title: "Ansprechpersonen: Ersthelfer, Sicherheitsbeauftragte, Fachkraft für Arbeitssicherheit, Betriebsarzt", sections: ["pflichten"], company: true },
  { id: "rundgang", title: "Rundgang durch die Räume, Laufwege", sections: [], company: true },
  { id: "psa", title: "Persönliche Schutzausrüstung, Lärm und Gehörschutz", sections: ["psa", "laerm"] },
];

export type BgStatus = "covered" | "company-done" | "company-open" | "off";

export interface BgCoverage {
  topic: BgTopic;
  status: BgStatus;
}

/**
 * Wie gut die gewählten Abschnitte die Mindestthemen abdecken. „company“-Themen kann nur der Betrieb füllen:
 * Sie gelten als erledigt, sobald eigene Hinweise oder eigene Abschnitte vorhanden sind.
 */
export function bgCoverage(sectionIds: string[], hasCompanyInput: boolean): BgCoverage[] {
  return TRAINING_BG_TOPICS.map((topic) => {
    const fixed = topic.sections.some((id) => sectionIds.includes(id));
    let status: BgStatus;
    if (topic.company) status = hasCompanyInput ? "company-done" : "company-open";
    else status = fixed ? "covered" : "off";
    return { topic, status };
  });
}

/* ---------- Eigene Inhalte der Firma ---------- */

export interface CustomSection {
  id: string;
  title: string;
  summary: string;
  slides: TrainingSlide[];
  questions: TrainingQuestion[];
}

export interface TrainingCustom {
  /** Eigene Abschnitte, z. B. „Unser Betriebsgelände“. */
  sections: CustomSection[];
  /** Eigene Fragen, die ans Ende eines festen Abschnitts gehängt werden (Schlüssel: Abschnitt). */
  questions: Record<string, TrainingQuestion[]>;
}

export const EMPTY_CUSTOM: TrainingCustom = { sections: [], questions: {} };

export const CUSTOM_LIMITS = { sections: 8, slides: 10, questionsPerSection: 8, extraQuestions: 5, options: 6 } as const;

const PICTO_ID = z.string().max(40).refine((id) => id in PICTOS, "Dieses Sicherheitszeichen gibt es nicht.");

const customQuestionSchema = z.object({
  id: z.string().regex(/^[a-z0-9-]{3,40}$/, "Die Frage hat eine ungültige Kennung."),
  text: z.string().trim().min(5, "Bitte formulieren Sie die Frage mit mindestens 5 Zeichen.").max(300, "Eine Frage darf höchstens 300 Zeichen lang sein."),
  options: z
    .array(z.string().trim().min(1, "Bitte füllen Sie alle Antworten aus oder entfernen Sie leere.").max(200, "Eine Antwort darf höchstens 200 Zeichen lang sein."))
    .min(2, "Jede Frage braucht mindestens zwei Antworten.")
    .max(CUSTOM_LIMITS.options, `Höchstens ${CUSTOM_LIMITS.options} Antworten je Frage.`),
  correct: z.array(z.number().int().min(0).max(CUSTOM_LIMITS.options - 1)).min(1, "Bitte markieren Sie mindestens eine richtige Antwort.").max(CUSTOM_LIMITS.options),
  picto: PICTO_ID.optional(),
});

const customSlideSchema = z.object({
  title: z.string().trim().min(2, "Jede Folie braucht eine Überschrift.").max(120, "Eine Überschrift darf höchstens 120 Zeichen lang sein."),
  text: z.string().trim().min(2, "Jede Folie braucht einen Text.").max(800, "Ein Folientext darf höchstens 800 Zeichen lang sein."),
  pictos: z.array(PICTO_ID).max(4, "Höchstens 4 Sicherheitszeichen je Folie.").optional(),
  scene: z.enum(["hello", "point", "alarm", "walk", "think", "check"]).optional(),
});

export const trainingCustomSchema = z.object({
  sections: z
    .array(
      z.object({
        id: z.string().regex(/^c-[a-z0-9]{4,30}$/, "Der Abschnitt hat eine ungültige Kennung."),
        title: z.string().trim().min(2, "Jeder eigene Abschnitt braucht einen Titel.").max(100, "Ein Titel darf höchstens 100 Zeichen lang sein."),
        summary: z.string().trim().max(200, "Die Kurzbeschreibung darf höchstens 200 Zeichen lang sein."),
        slides: z.array(customSlideSchema).min(1, "Jeder eigene Abschnitt braucht mindestens eine Folie.").max(CUSTOM_LIMITS.slides, `Höchstens ${CUSTOM_LIMITS.slides} Folien je Abschnitt.`),
        questions: z.array(customQuestionSchema).max(CUSTOM_LIMITS.questionsPerSection, `Höchstens ${CUSTOM_LIMITS.questionsPerSection} Fragen je Abschnitt.`),
      }),
    )
    .max(CUSTOM_LIMITS.sections, `Höchstens ${CUSTOM_LIMITS.sections} eigene Abschnitte.`),
  questions: z.record(z.string().max(60), z.array(customQuestionSchema).max(CUSTOM_LIMITS.extraQuestions, `Höchstens ${CUSTOM_LIMITS.extraQuestions} eigene Fragen je Abschnitt.`)),
});

/** Prüft, was sich nicht im Schema ausdrücken lässt: eindeutige Kennungen, sinnvolle Antworten. Gibt eine Meldung zurück oder null. */
export function checkCustom(custom: TrainingCustom, course: TrainingCourse): string | null {
  const fixedIds = new Map(course.sections.map((s) => [s.id, new Set(s.questions.map((q) => q.id))]));
  const sectionIds = new Set<string>();
  const checkQuestion = (q: TrainingQuestion, taken: Set<string>): string | null => {
    if (taken.has(q.id)) return "Zwei Fragen haben dieselbe Kennung. Bitte laden Sie die Seite neu und versuchen Sie es noch einmal.";
    taken.add(q.id);
    if (q.correct.some((c) => c >= q.options.length)) return `Bei der Frage „${q.text}“ ist eine richtige Antwort markiert, die es nicht gibt.`;
    if (new Set(q.correct).size !== q.correct.length) return `Bei der Frage „${q.text}“ ist eine Antwort doppelt markiert.`;
    if (q.correct.length >= q.options.length) return `Bei der Frage „${q.text}“ darf nicht jede Antwort richtig sein.`;
    return null;
  };
  for (const s of custom.sections) {
    if (sectionIds.has(s.id) || fixedIds.has(s.id)) return "Zwei Abschnitte haben dieselbe Kennung. Bitte laden Sie die Seite neu und versuchen Sie es noch einmal.";
    sectionIds.add(s.id);
    const taken = new Set<string>();
    for (const q of s.questions) {
      const err = checkQuestion(q, taken);
      if (err) return err;
    }
  }
  for (const [sectionId, list] of Object.entries(custom.questions)) {
    const own = fixedIds.get(sectionId);
    if (!own) return "Eigene Fragen gehören zu einem Abschnitt, den es nicht gibt.";
    const taken = new Set(own);
    for (const q of list) {
      const err = checkQuestion(q, taken);
      if (err) return err;
    }
  }
  return null;
}

/* ---------- Welche Abschnitte jemand bekommt ---------- */

/** Abschnitte der ersten Fassung, für Teilnahmen, die vor der Modulauswahl begonnen wurden. */
export const LEGACY_SECTION_IDS = ["pflichten", "notfall", "brandschutz", "psa", "ordnung", "elektrisch"];

/** Voreinstellung einer Firma, die nichts gewählt hat: wie in der ersten Fassung. */
export const DEFAULT_MODULES = ["psa", "elektrisch"];

/** Pflichtabschnitte plus die eingeschalteten Module (feste und eigene), in der Reihenfolge der Schulung. */
export function planSectionIds(course: TrainingCourse, custom: TrainingCustom, modules: string[]): string[] {
  const on = new Set(modules);
  return [
    ...course.sections.filter((s) => s.kind === "basis" || on.has(s.id)).map((s) => s.id),
    ...custom.sections.filter((s) => on.has(s.id)).map((s) => s.id),
  ];
}

/** Macht aus Kennungen die Abschnitte mit allem Inhalt: feste Abschnitte samt eigener Zusatzfragen, eigene Abschnitte. Unbekannte fallen weg. */
export function resolveSections(course: TrainingCourse, custom: TrainingCustom, ids: string[]): TrainingSection[] {
  const fixed = new Map(course.sections.map((s) => [s.id, s]));
  const own = new Map(custom.sections.map((s) => [s.id, s]));
  return ids.flatMap((id): TrainingSection[] => {
    const f = fixed.get(id);
    if (f) return [{ ...f, questions: [...f.questions, ...(custom.questions[id] ?? [])] }];
    const c = own.get(id);
    if (c) return [{ id: c.id, kind: "custom", title: c.title, summary: c.summary || "Hinweise aus Ihrem Betrieb", slides: c.slides, questions: c.questions, noteHint: "" }];
    return [];
  });
}

/* ---------- Was der Browser zu sehen bekommt ---------- */

export interface PublicQuestion {
  id: string;
  text: string;
  options: string[];
  multiple: boolean;
  picto?: string;
}

export interface PublicSection extends Omit<TrainingSection, "questions"> {
  questions: PublicQuestion[];
}

export interface PublicCourse extends Omit<TrainingCourse, "sections"> {
  sections: PublicSection[];
}

/** Schulung ohne die richtigen Antworten. */
export function publicCourse(c: TrainingCourse): PublicCourse {
  return {
    ...c,
    sections: c.sections.map((s) => ({
      ...s,
      questions: s.questions.map((q) => ({ id: q.id, text: q.text, options: q.options, multiple: q.correct.length > 1, ...(q.picto ? { picto: q.picto } : {}) })),
    })),
  };
}

export type TrainingAnswers = Record<string, number[]>;

/** Bewertet einen Abschnitt. Bestanden ist er nur, wenn alle Fragen ganz richtig beantwortet sind. */
export function gradeSection(section: TrainingSection, answers: TrainingAnswers): { correct: number; total: number; wrong: string[]; passed: boolean } {
  const wrong: string[] = [];
  for (const q of section.questions) {
    const given = [...new Set(answers[q.id] ?? [])].sort((a, b) => a - b);
    const right = [...q.correct].sort((a, b) => a - b);
    if (given.length !== right.length || given.some((v, i) => v !== right[i])) wrong.push(q.id);
  }
  const total = section.questions.length;
  return { correct: total - wrong.length, total, wrong, passed: wrong.length === 0 };
}

/* ---------- Teilnahmen ---------- */

export interface SectionProgress {
  watched: boolean;
  passed: boolean;
  /** Anzahl abgegebener Antwortrunden. */
  tries: number;
  passedAt?: string;
}

export type TrainingProgress = Record<string, SectionProgress>;

export type AttemptStatus = "running" | "passed";

export interface TrainingAttempt {
  id: string;
  courseId: string;
  courseTitle: string;
  courseVersion: number;
  participantName: string;
  /** Ob die Teilnahme zum eigenen Konto gehört. */
  mine: boolean;
  status: AttemptStatus;
  progress: TrainingProgress;
  /** Abschnitte dieser Teilnahme, in der Reihenfolge der Schulung. */
  sectionIds: string[];
  startedAt: string;
  passedAt: string | null;
  validUntil: string | null;
  hasCertificate: boolean;
}

export interface TrainingVideoInfo {
  sectionId: string;
  name: string;
  size: number;
  /** Ändert sich beim Austausch, damit der Browser nicht das alte Video zeigt. */
  version: string;
}

export type TrainingMandatory = "all" | "selected";

export interface TrainingSettings {
  instructor: string;
  contact: string;
  notes: Record<string, string>;
  validMonths: 6 | 12;
  videos: TrainingVideoInfo[];
  /** Eingeschaltete Module (feste, optionale und eigene Abschnitte); Pflichtabschnitte gelten immer. */
  enabledModules: string[];
  /** Ob alle Mitarbeiter die Schulung machen müssen oder nur ausgewählte. */
  mandatory: TrainingMandatory;
  /** Eigene Inhalte samt richtigen Antworten; nur für Inhaber und Admins. */
  custom?: TrainingCustom;
}

/** Ein Abschnitt im Katalog, für die Einrichtung. */
export interface CatalogSection {
  id: string;
  kind: SectionKind;
  title: string;
  summary: string;
  topics: string[];
  noteHint: string;
  slides: number;
  questions: number;
  pictos: string[];
}

export interface TrainingPlan {
  /** Ob die angemeldete Person diese Schulung machen muss. */
  required: boolean;
  sectionIds: string[];
}

export interface TrainingCourseInfo {
  /** Die Schulung so, wie die angemeldete Person sie bekommt: Pflichtabschnitte, gewählte Module, eigene Abschnitte. */
  course: PublicCourse;
  catalog: CatalogSection[];
  settings: TrainingSettings;
  plan: TrainingPlan;
  /** Laufende oder zuletzt bestandene Teilnahme der angemeldeten Person. */
  mine: TrainingAttempt | null;
}

export interface TrainingListResponse {
  courses: TrainingCourseInfo[];
  canManage: boolean;
}

export interface TrainingTeamRow {
  userId: string | null;
  name: string;
  courseId: string;
  status: "passed" | "due" | "running" | "open";
  lastPassedAt: string | null;
  validUntil: string | null;
  attemptId: string | null;
}

export interface TrainingOverview {
  team: TrainingTeamRow[];
  /** Alle bestandenen Teilnahmen, neueste zuerst. */
  certificates: TrainingAttempt[];
}

export interface TrainingAssignment {
  userId: string;
  name: string;
  required: boolean;
  /** Eigene Auswahl der Module; null heißt: wie die Firma voreingestellt hat. */
  modules: string[] | null;
}

export interface AnswerResult {
  passed: boolean;
  correct: number;
  total: number;
  wrong: string[];
  attempt: TrainingAttempt;
}

export const MAX_TRAINING_VIDEO_MB = 300;

/* ---------- Eingaben ---------- */

export const trainingSettingsSchema = z.object({
  instructor: z.string().trim().max(120, "Bitte höchstens 120 Zeichen."),
  contact: z.string().trim().max(160, "Bitte höchstens 160 Zeichen."),
  notes: z.record(z.string().max(60), z.string().max(1500, "Ein Hinweis darf höchstens 1500 Zeichen lang sein.")),
  validMonths: z.union([z.literal(6), z.literal(12)]),
  enabledModules: z.array(z.string().max(60)).max(60).optional(),
  mandatory: z.enum(["all", "selected"]).optional(),
  custom: trainingCustomSchema.optional(),
});

export const trainingAssignmentsSchema = z.object({
  assignments: z
    .array(z.object({ userId: z.uuid(), required: z.boolean(), modules: z.array(z.string().max(60)).max(60).nullable() }))
    .max(500, "Zu viele Einträge auf einmal."),
});

export const trainingStartSchema = z.object({
  /** Nur Inhaber und Admins: Teilnahme für eine Person ohne eigenes Konto, z. B. Aushilfe am Firmen-PC. */
  participantName: z.string().trim().min(2, "Bitte geben Sie Vor- und Nachnamen an.").max(120, "Bitte höchstens 120 Zeichen.").optional(),
});

export const trainingAnswersSchema = z.object({
  answers: z.record(z.string().max(60), z.array(z.number().int().min(0).max(20)).max(20)),
});

export const trainingCompleteSchema = z.object({
  confirm: z.literal(true, { error: "Bitte bestätigen Sie, dass Sie die Inhalte verstanden haben." }),
  signature: z.object({ image: z.string().max(300_000), name: z.string().max(120), signedAt: z.string().max(40) }),
});
